import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import DriverForm from "./Driver/DriverForm";
import DriverAvailability from "./Driver/DriverAvailability";
import DriverReviewsModal from "./Driver/DriverReviewsModal";
import BotaoAtivarDesativarMotorista from "../../../core/common/components/BotaoAtivarDesativarMotorista";

type Driver = {
  id?: number;
  nome: string;
  naturalidade: string;
  pais: string;
  documento: string;
  telefone: string;
  email: string;
  morada: string;
  numeroCartaConducao: string;
  dataExpedicao: string;
  dataValidade: string;
  categoria: string;
  idiomas: string[];
  registoCriminal: string;
  descricao: string;   // NOVO
  foto?: File | null;  // NOVO (para upload)
  fotoUrl?: string | null; // url vinda do backend

  // 🔥 Campos relacionados ao veículo
  veiculoId?: number | null;
  veiculoModelo?: string | null;
  veiculoPlaca?: string | null;

  ativo?: boolean;
};

type AvaliacaoDTO = {
  id: number;
  nota: number;
  comentario?: string;
  usuarioNome?: string;
  usuarioFotoUrl?: string;
  fotosPorPonto?: { fotoId: number; ponto: string; url: string }[];
  fotos?: number[];
};

type MotoristaComAvaliacoes = Driver & {
  avaliacoes: AvaliacaoDTO[];
};

const languageOptions = [
  { value: "English", label: "English" },
  { value: "Italian", label: "Italian" },
  { value: "French", label: "French" },
  { value: "German", label: "German" },
  { value: "Portuguese", label: "Portuguese" },
  { value: "Russian", label: "Russian" },
  { value: "Hebrew", label: "Hebrew" },
  { value: "Mandarin", label: "Mandarin" },
  { value: "Japanese", label: "Japanese" },
  { value: "Ukrainian", label: "Ukrainian" },
  // adicionais
  { value: "Spanish", label: "Spanish" },
  { value: "Dutch", label: "Dutch" },
  { value: "Swedish", label: "Swedish" },
  { value: "Norwegian", label: "Norwegian" },
  { value: "Danish", label: "Danish" },
  { value: "Polish", label: "Polish" },
  { value: "Greek", label: "Greek" },
  { value: "Turkish", label: "Turkish" },
  { value: "Czech", label: "Czech" },
  { value: "Hungarian", label: "Hungarian" },
  { value: "Romanian", label: "Romanian" },
  { value: "Finnish", label: "Finnish" },
  { value: "Slovak", label: "Slovak" },
  { value: "Bulgarian", label: "Bulgarian" },
  { value: "Croatian", label: "Croatian" },
  { value: "Irish", label: "Irish" },
  { value: "Welsh", label: "Welsh" },
  { value: "Scottish Gaelic", label: "Scottish Gaelic" },
  { value: "Basque", label: "Basque" },
  { value: "Catalan", label: "Catalan" },
  { value: "Galician", label: "Galician" },
];

const LABELS: Partial<Record<keyof Driver, string>> = {
  id: "ID",
  nome: "Name",
  naturalidade: "Place of Birth",
  pais: "Country",
  documento: "ID Document (BI)",
  telefone: "Phone",
  email: "Email",
  morada: "Address",
  numeroCartaConducao: "Driving License Number",
  dataExpedicao: "License Issue Date",
  dataValidade: "License Expiry Date",
  categoria: "License Category",
  idiomas: "Languages",
  registoCriminal: "Criminal Record (EU compliant)",
  descricao: "Description",
  foto: "Photo",
};


const DriversSection = ({ empresaId }: { empresaId: number }) => {
  const BASE_URL = process.env.REACT_APP_API_URL;
  const [drivers, setDrivers] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [veiculos, setVeiculos] = useState<{ id: number; modelo: string; placa: string }[]>([]);
  const [hasCriminalRecord, setHasCriminalRecord] = useState(false);
  const [editHasCriminalRecord, setEditHasCriminalRecord] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const emptyDriver: Driver = {
    nome: "",
    naturalidade: "",
    pais: "",
    documento: "",
    numeroCartaConducao: "",
    telefone: "",
    email: "",
    morada: "",
    dataExpedicao: "",
    dataValidade: "",
    categoria: "",
    idiomas: [],
    registoCriminal: "",
    descricao: "",   // NOVO
    foto: null,      // NOVO
    ativo: true,
  };


  const [newDriver, setNewDriver] = useState<Driver>({ ...emptyDriver });
  const [editDriver, setEditDriver] = useState<Driver>({ ...emptyDriver });
  const [previewFoto, setPreviewFoto] = useState<string | null>(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // Novo: status da empresa
  const [empresaStatus, setEmpresaStatus] = useState<string | null>(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState<string | null>(null);
  const [empresaLoading, setEmpresaLoading] = useState<boolean>(true);

  const loadDrivers = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/empresas/${empresaId}/motoristas`, { headers });
      const motoristasData: Driver[] = res.data;

      // 🔹 Agora buscar as avaliações de cada motorista
      const motoristasComAvaliacoes: MotoristaComAvaliacoes[] = await Promise.all(
        motoristasData.map(async (m) => {
          const avalRes = await axios.get<AvaliacaoDTO[]>(`${BASE_URL}/avaliacoes/motorista/${m.id}`);
          return {
            ...m,
            avaliacoes: avalRes.data,
          };
        })
      );

      setDrivers(motoristasComAvaliacoes);
    } catch (err) {
      console.error("Failed to load drivers or reviews", err);
    }
  };

  const loadVeiculos = () => {
    axios
      .get(`${BASE_URL}/empresas/${empresaId}/veiculos`, { headers })
      .then((res) => setVeiculos(res.data))
      .catch(() => alert("Failed to load vehicles."));
  };

  useEffect(() => {
    if (empresaId) {
      loadDrivers();
      loadVeiculos();
    }
  }, [empresaId]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/empresas/minha`, { headers })
      .then((res) => {
        setEmpresaStatus(res.data.status);
        setMotivoRejeicao(res.data.motivoRejeicao || null);
      })
      .catch((err) => {
        console.error("Erro ao carregar status da empresa", err);
        setEmpresaStatus("ERRO"); // bloqueia por segurança
      })
      .finally(() => {
        setEmpresaLoading(false);
      });
  }, []);

  const handleAdd = async (driver: Driver) => {
    try {
      const res = await axios.post(`${BASE_URL}/empresas/${empresaId}/motoristas`, driver, { headers });
      const motoristaId = res.data.id;

      if (driver.foto) {
        const formData = new FormData();
        formData.append("file", driver.foto);
        await axios.put(`${BASE_URL}/empresas/motoristas/${motoristaId}/foto`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
      }

      loadDrivers();
      alert("Driver added successfully.");
    } catch (e) {
      console.error(e);
      alert("Error while saving driver.");
    }
  };

  const handleDelete = (id: number) => {
    if (!window.confirm("Are you sure you want to delete this driver?")) return;

    axios
      .delete(`${BASE_URL}/motoristas/${id}`, { headers })
      .then(() => {
        loadDrivers();
        alert("Driver deleted successfully.");
      })
      .catch(() => alert("Error while deleting driver."));
  };

  const handleUpdate = async (id: number) => {

    try {
      // Atualiza dados básicos
      await axios.put(`${BASE_URL}/empresas/motoristas/${id}`, editDriver, { headers });

      // Se tiver foto nova, faz upload separado
      if (editDriver.foto) {
        const formData = new FormData();
        formData.append("file", editDriver.foto);

        await axios.put(`${BASE_URL}/empresas/motoristas/${id}/foto`, formData, {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      loadDrivers();
      setEditId(null);
      setPreviewFoto(null);
      alert("Driver updated successfully.");
    } catch (e) {
      console.error(e);
      alert("Error while updating driver.");
    }
  };

  const atribuirMotorista = (motoristaId: number, veiculoId: number | null) => {
    axios
      .post(
        `${BASE_URL}/empresas/atribuir-motorista`,
        { motoristaId, veiculoId },
        { headers }
      )
      .then(() => {
        loadDrivers();
        alert("Driver vehicle assignment updated.");
      })
      .catch(() => alert("Error assigning/removing vehicle from driver."));
  };

  return (
    <div className="settings-content mt-3">
      <h6 className="fs-16 mb-3">Drivers</h6>

      {/* Banner informativo se empresa não aprovada */}
      {empresaLoading ? (
        <div className="alert alert-info">Checking company status...</div>
      ) : empresaStatus === "APROVADA" ? (
        <div className="alert alert-success">Approved company — you can register drivers.</div>
      ) : empresaStatus === "REJEITADA" ? (
        <div className="alert alert-danger">
          Company REJECTED — registration blocked. <br />
          <strong>Reason:</strong> {motivoRejeicao || "No reason provided."}
          Go to the company information page (on the dashboard: settings {">"} company info) to update your data and send it for review again.
        </div>
      ) : (
        <div className="alert alert-warning">
          Company NOT approved — driver and vehicle registration is blocked until approval.
        </div>
      )}

      <button
        className="btn btn-sm btn-outline-primary mb-3"
        onClick={() => {
          if (empresaLoading) {
            alert("Verificando status da empresa. Aguarde...");
            return;
          }
          if (empresaStatus === "REJEITADA") {
            alert(`Sua empresa foi rejeitada. Motivo: ${motivoRejeicao || "Motivo não informado"}`);
            return;
          }
          if (empresaStatus !== "APROVADA") {
            alert("Sua empresa ainda não foi aprovada. Não é possível cadastrar motoristas.");
            return;
          }
          setShowForm((s) => !s);
        }}
        disabled={empresaLoading || empresaStatus !== "APROVADA"}
        title={
          empresaLoading
            ? "Verificando status da empresa..."
            : empresaStatus === "APROVADA"
              ? "Adicionar motorista"
              : empresaStatus === "REJEITADA"
                ? "Sua empresa foi rejeitada"
                : "Sua empresa ainda não está aprovada"
        }
      >
        {showForm ? "Cancel" : "Add Driver"}
      </button>

      {showForm && (
        <DriverForm
          initialData={emptyDriver}
          errors={errors}
          onSubmit={(driver) => {
            handleAdd(driver); // recebe driver do form
            setShowForm(false);
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      <div className="row">
        {drivers.length > 0 ? (
          drivers.map((d) => (
            <div className="col-md-6 mb-3" key={d.id}>
              <div className="card h-100">
                <div className="card-body">
                  {editId === d.id ? (
                    <>
                      {(Object.keys(editDriver) as (keyof Driver)[]).map((field) => {

                        if (["veiculoId", "veiculoModelo", "veiculoPlaca", "id", "fotoUrl"].includes(field)) {
                          return null;
                        }
                        if (field === "idiomas") {
                          return (
                            <div className="mb-2" key={field}>
                              <label className="form-label">Languages Spoken</label>
                              <Select
                                isMulti
                                options={languageOptions}
                                value={editDriver.idiomas.map((idioma) => ({ label: idioma, value: idioma }))}
                                onChange={(selected) =>
                                  setEditDriver({
                                    ...editDriver,
                                    idiomas: Array.isArray(selected) ? selected.map((s) => s.value) : [],
                                  })
                                }
                              />
                            </div>
                          );
                        }

                        if (field === "foto") {
                          return (
                            <div className="mb-2" key={field}>
                              <label className="form-label">{LABELS.foto}</label>
                              <input
                                type="file"
                                accept="image/*"
                                className="form-control"
                                onChange={(e) => {
                                  const file = e.target.files ? e.target.files[0] : null;
                                  setEditDriver({ ...editDriver, foto: file });
                                  setPreviewFoto(file ? URL.createObjectURL(file) : null);
                                }}
                              />

                              {previewFoto && (
                                <div className="mt-2 text-center">
                                  <img
                                    src={previewFoto}
                                    alt="Preview"
                                    style={{ width: 120, height: 120, borderRadius: "50%", objectFit: "cover" }}
                                  />
                                </div>
                              )}

                              {!previewFoto && editDriver.fotoUrl && (
                                <div className="mt-2 text-center">
                                  <img
                                    src={`${BASE_URL}${editDriver.fotoUrl}`}
                                    alt="Current"
                                    style={{ width: 120, height: 120, borderRadius: "50%", objectFit: "cover" }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div className="mb-2" key={field}>
                            <label className="form-label">{LABELS[field]}</label>
                            {field === "registoCriminal" ? (
                              <div className="mb-2">
                                <div className="form-check mb-2">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`edit-${field}-check`}
                                    checked={editHasCriminalRecord}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setEditHasCriminalRecord(checked);
                                      if (!checked)
                                        setEditDriver({
                                          ...editDriver,
                                          registoCriminal: "",
                                        });
                                    }}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`edit-${field}-check`}
                                  >
                                    Yes
                                  </label>
                                </div>

                                {editHasCriminalRecord && (
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter criminal record details"
                                    value={editDriver.registoCriminal}
                                    onChange={(e) =>
                                      setEditDriver({
                                        ...editDriver,
                                        registoCriminal: e.target.value,
                                      })
                                    }
                                  />
                                )}
                              </div>
                            ) : (
                              <input
                                type={
                                  field === "dataExpedicao" ||
                                    field === "dataValidade"
                                    ? "date"
                                    : "text"
                                }
                                className={`form-control ${errors[field] ? "is-invalid" : ""
                                  }`}
                                value={editDriver[field] as string}
                                onChange={(e) =>
                                  setEditDriver({
                                    ...editDriver,
                                    [field]: e.target.value,
                                  })
                                }
                              />
                            )}
                            {errors[field] && (
                              <div className="invalid-feedback">{errors[field]}</div>
                            )}
                          </div>
                        );
                      })}

                      {/* Select de veículo no modo edição */}
                      <div className="mb-2">
                        <label className="form-label">
                          <strong>Vehicle:</strong>
                        </label>
                        <Select
                          placeholder="Assign vehicle..."
                          options={veiculos.map((v) => ({
                            label: `${v.modelo} - ${v.placa}`,
                            value: v.id,
                          }))}
                          value={
                            editDriver.veiculoId
                              ? {
                                label: `${editDriver.veiculoModelo} - ${editDriver.veiculoPlaca}`,
                                value: editDriver.veiculoId,
                              }
                              : null
                          }
                          onChange={(selected: any) => {
                            setEditDriver({
                              ...editDriver,
                              veiculoId: selected ? selected.value : null,
                              veiculoModelo: selected
                                ? veiculos.find((v) => v.id === selected.value)
                                  ?.modelo || ""
                                : "",
                              veiculoPlaca: selected
                                ? veiculos.find((v) => v.id === selected.value)
                                  ?.placa || ""
                                : "",
                            });
                          }}
                          isClearable
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Foto do motorista + status */}
                      <div className="mb-2 text-center position-relative" style={{ display: "inline-block" }}>
                        {d.fotoUrl ? (
                          <img
                            src={`${BASE_URL}${d.fotoUrl}`}
                            alt={d.nome}
                            style={{
                              width: "120px",
                              height: "120px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "120px",
                              height: "120px",
                              borderRadius: "50%",
                              backgroundColor: "#ccc",
                              display: "inline-block",
                            }}
                          />
                        )}

                        {/* 🔴/🟢 Bolinha de status */}
                        {/* Bolinha de status */}
                        <span
                          style={{
                            position: "absolute",
                            bottom: 5,
                            right: 5,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: "2px solid white",
                            backgroundColor: d.ativo !== false ? "green" : "red",
                            display: "inline-block",
                          }}
                          title={d.ativo ? "Ativo" : "Inativo"}
                        />
                      </div>

                      {/* Informações do motorista */}
                      <h6>{d.nome}</h6>
                      <p><strong>Email:</strong> {d.email}</p>
                      <p><strong>ID / Document:</strong> {d.documento}</p>
                      <p><strong>Phone:</strong> {d.telefone}</p>
                      <p><strong>Driving License #:</strong> {d.numeroCartaConducao}</p>
                      <p><strong>Issue Date:</strong> {d.dataExpedicao}</p>
                      <p><strong>Expiry Date:</strong> {d.dataValidade}</p>
                      <p><strong>Category:</strong> {d.categoria}</p>
                      <p>
                        <strong>Languages:</strong>{" "}
                        {(Array.isArray(d.idiomas) ? d.idiomas : [d.idiomas]).filter(Boolean).join(", ")}
                      </p>
                      <p>
                        <strong>Criminal Record:</strong> {d.registoCriminal ? "Yes" : "No"}
                      </p>

                      {/* Resumo da avaliação */}
                      {d.avaliacoes && d.avaliacoes.length > 0 ? (
                        <p
                          style={{ cursor: "pointer" }}
                          onClick={() => setModalOpen(true)}
                          className="d-inline-flex align-items-center gap-1"
                        >
                          <strong>Average Rating:</strong>{" "}
                          <span className="badge badge-warning p-1">
                            <i className="isax isax-star text-black" />{" "}
                            <span className="text-black">
                              {(
                                d.avaliacoes.reduce((sum: number, a: AvaliacaoDTO) => sum + (a.nota || 0), 0) /
                                d.avaliacoes.length
                              ).toFixed(1)} ({d.avaliacoes.length} reviews)
                            </span>
                          </span>
                          <small className="text-muted ms-1">Ver detalhes</small>
                        </p>
                      ) : (
                        <p><strong>Average Rating:</strong> No reviews yet</p>
                      )}

                      {/* ✅ Botão de ativar/desativar motorista */}
                      <div className="text-center my-3">
                        <BotaoAtivarDesativarMotorista
                          motoristaId={d.id}
                          ativo={d.ativo}
                          onStatusChange={(novoStatus) => {
                            // Atualiza o estado local corretamente
                            setDrivers((prevDrivers) =>
                              prevDrivers.map((m) =>
                                m.id === d.id ? { ...m, ativo: novoStatus } : m
                              )
                            );
                          }}
                        />
                      </div>

                      {/* Modal de avaliações */}
                      {d.avaliacoes && d.avaliacoes.length > 0 && (
                        <DriverReviewsModal
                          motoristaId={d.id}       // Passando só o id
                          open={modalOpen}
                          onClose={() => setModalOpen(false)}
                          baseUrl={BASE_URL!}
                        />
                      )}

                      {/* Select de veículo sempre visível */}
                      <div className="mb-2">
                        <label className="form-label">
                          <strong>Vehicle:</strong>
                        </label>
                        <Select
                          placeholder="Assign vehicle..."
                          options={veiculos.map((v) => ({
                            label: `${v.modelo} - ${v.placa}`,
                            value: v.id,
                          }))}
                          value={
                            d.veiculoId
                              ? { label: `${d.veiculoModelo} - ${d.veiculoPlaca}`, value: d.veiculoId }
                              : null
                          }
                          onChange={(selected: any) => {
                            atribuirMotorista(d.id, selected ? selected.value : null);
                          }}
                          isClearable
                        />
                      </div>

                      {/* Disponibilidade */}
                      <DriverAvailability driverId={d.id} veiculoId={d.veiculoId} />
                    </>
                  )}
                </div>
                <div className="card-footer text-end">
                  {editId === d.id ? (
                    <>
                      <button
                        className="btn btn-sm btn-success me-2"
                        onClick={() => handleUpdate(d.id)}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setEditId(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          setEditId(d.id);
                          setEditDriver({ ...d });
                          setEditHasCriminalRecord(!!d.registoCriminal);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(d.id)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>No drivers registered yet.</p>
        )}
      </div>
    </div>
  );
};

export default DriversSection;
